import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { DownloadOutlined, SettingOutlined } from "@ant-design/icons";
import { Button, DatePicker, Typography, notification } from "antd";
import dayjs from "dayjs";
import { useAuth } from "../hooks/useAuth";
import {
  fetchSafetyInspectionByDate,
  fetchSafetyQuestions,
  generateSafetyInspectionPdf,
  updateSafetyInspection,
} from "../services/safetyAPI";
import queryClient from "../queryClient";
import { DATE_PICKER_FORMAT } from "../constants/dateConstants";
import SafetyQuestionsTable from "../components/Safety/SafetyQuestionsTable";
import SafetyQuestionsModal from "../components/Safety/SafetyQuestionsModal";
import NotesTable from "../components/DailyReports/NotesTable";
import AttachmentsSection from "../components/AttachmentsSection";
import "../components/DailyReports/dailyReport.css";

const { Title, Text } = Typography;

const Safety = () => {
  const { selectedProject } = useAuth();
  const projectId = selectedProject?.project_id;
  const [date, setDate] = useState(dayjs(new Date()));
  const [localInspection, setLocalInspection] = useState({
    items: [],
    notes: [],
    attachments: [],
  });
  const [isQuestionsModalVisible, setIsQuestionsModalVisible] = useState(false);

  const dateStr = date?.format?.("YYYY-MM-DD");

  const {
    data: inspection,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["safetyInspection", projectId, dateStr],
    queryFn: () =>
      fetchSafetyInspectionByDate({
        projectId,
        date,
      }),
    enabled: !!projectId && !!date,
  });

  const { data: questionsData } = useQuery({
    queryKey: ["safetyQuestions", projectId],
    queryFn: () => fetchSafetyQuestions(projectId),
    enabled: !!projectId,
  });

  const hasQuestions = (questionsData?.questions?.length || 0) > 0;
  const questionsButtonLabel = hasQuestions
    ? "Update Questions"
    : "Add Questions";

  useEffect(() => {
    // Don't keep previous project's report while the new one loads.
    setLocalInspection({
      items: [],
      notes: [],
      attachments: [],
    });
  }, [projectId]);

  useEffect(() => {
    if (inspection) {
      setLocalInspection(inspection);
    }
  }, [inspection]);

  const inspectionQueryKey = ["safetyInspection", projectId, dateStr];

  const { mutate: updateInspectionMutation, mutateAsync: updateInspectionAsync } =
    useMutation({
    mutationFn: ({ inspectionId, inspection: payload }) =>
      updateSafetyInspection(inspectionId, payload),
    onSuccess: (updated, variables) => {
      const payload = variables?.inspection || {};
      const requestId = variables?.inspectionId;
      const isCurrentInspection = (prev) =>
        prev?._id &&
        requestId &&
        String(prev._id) === String(requestId);

      // Answer patches: merge only the patched question(s) into the current
      // snapshot. Never invalidate/refetch here — on slow networks a stale
      // from-range response can land after pending was cleared and wipe a
      // newer answer on another question (Alper 3G repro).
      if (payload.answer_patches?.length) {
        const patchById = Object.fromEntries(
          payload.answer_patches.map((patch) => [
            String(patch.question_id),
            patch.answer,
          ])
        );
        const mergePatchedItems = (items) =>
          (items || []).map((item) => {
            const qid = String(item.question_id);
            if (!Object.prototype.hasOwnProperty.call(patchById, qid)) {
              return item;
            }
            return { ...item, answer: patchById[qid] };
          });

        setLocalInspection((prev) => {
          if (!isCurrentInspection(prev)) return prev;
          return {
            ...prev,
            updated_at: updated.updated_at,
            items: mergePatchedItems(prev.items),
          };
        });
        queryClient.setQueryData(inspectionQueryKey, (prev) => {
          if (!prev || !isCurrentInspection(prev)) return prev;
          return {
            ...prev,
            updated_at: updated.updated_at,
            items: mergePatchedItems(prev.items),
          };
        });
        return;
      }

      // Don't clobber optimistic answer clicks with a stale full replace.
      // Ignore responses that no longer match the on-screen inspection
      // (range/project changed while notes/attachments save was in flight).
      if (payload.notes || payload.attachments) {
        setLocalInspection((prev) => {
          if (!isCurrentInspection(prev)) return prev;
          return {
            ...prev,
            notes: updated.notes ?? prev.notes,
            attachments: updated.attachments ?? prev.attachments,
            updated_at: updated.updated_at,
            // Keep local answers — a refetch can race in-flight answer patches.
            items: prev.items || updated.items,
          };
        });
        queryClient.setQueryData(inspectionQueryKey, (prev) => {
          if (!prev || !isCurrentInspection(prev)) return prev;
          return {
            ...prev,
            notes: updated.notes ?? prev.notes,
            attachments: updated.attachments ?? prev.attachments,
            updated_at: updated.updated_at,
            items: prev.items || updated.items,
          };
        });
        return;
      }

      if (payload.items) {
        setLocalInspection((prev) => {
          if (!isCurrentInspection(prev)) return prev;
          return {
            ...prev,
            updated_at: updated.updated_at,
          };
        });
        queryClient.setQueryData(inspectionQueryKey, (prev) => {
          if (!prev || !isCurrentInspection(prev)) return prev;
          return {
            ...prev,
            updated_at: updated.updated_at,
          };
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update safety report",
        description:
          error.response?.data?.detail || error.message || "Unknown error",
      });
      refetch();
    },
  });

  const handleAnswerPersisted = (qid, answer, targetInspectionId, updated) => {
    const mergeItem = (prev) => {
      if (!prev?._id || String(prev._id) !== String(targetInspectionId)) {
        return prev;
      }
      return {
        ...prev,
        updated_at: updated?.updated_at ?? prev.updated_at,
        items: (prev.items || []).map((item) =>
          String(item.question_id) === String(qid)
            ? { ...item, answer }
            : item
        ),
      };
    };
    setLocalInspection(mergeItem);
    queryClient.setQueryData(inspectionQueryKey, (prev) =>
      prev ? mergeItem(prev) : prev
    );
  };

  const { mutate: generatePdfMutation, isPending: isGeneratingPdf } =
    useMutation({
      mutationFn: async ({ inspectionId }) => {
        const exportKey = "preparingSafetyPdf";
        notification.info({
          key: exportKey,
          message: "Preparing PDF...",
          description: "Your safety inspection PDF is being generated.",
          duration: 0,
        });
        try {
          const fileUrl = await generateSafetyInspectionPdf(inspectionId);
          if (!fileUrl) {
            throw new Error("PDF URL is missing");
          }
          notification.destroy(exportKey);
          const opened = window.open(fileUrl, "_blank", "noopener,noreferrer");
          if (!opened) {
            notification.success({
              message: "PDF ready",
              description: (
                <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                  Click here to open the PDF
                </a>
              ),
              duration: 8,
            });
          } else {
            notification.success({ message: "PDF opened in a new tab" });
          }
          return fileUrl;
        } catch (error) {
          notification.destroy(exportKey);
          throw error;
        }
      },
      onError: (error) => {
        notification.error({
          message: "Failed to generate PDF",
          description:
            error.response?.data?.detail || error.message || "Unknown error",
        });
      },
    });

  const handleDateChange = (value) => {
    if (!value) return;
    setLocalInspection({
      items: [],
      notes: [],
      attachments: [],
    });
    setDate(value);
  };

  const handleAttachmentsChange = (attachments) => {
    if (!localInspection?._id) return;
    setLocalInspection((prev) => ({ ...prev, attachments }));
    updateInspectionMutation({
      inspectionId: localInspection._id,
      inspection: { attachments },
    });
  };

  const handleGeneratePdf = () => {
    if (isLoading || isGeneratingPdf) return;
    if (!localInspection?._id) {
      notification.error({
        message: "Safety report not ready",
        description: "Wait for the report to load before exporting the PDF.",
      });
      return;
    }
    generatePdfMutation({
      inspectionId: localInspection._id,
    });
  };

  const handleQuestionsModalClose = () => {
    setIsQuestionsModalVisible(false);
    refetch();
    queryClient.invalidateQueries({
      queryKey: ["safetyQuestions", projectId],
    });
  };

  return (
    <div className="daily-report-page">
      <div className="daily-report-header">
        <h1 className="daily-report-title">Safety</h1>

        <div className="daily-report-toolbar">
          <div className="daily-report-toolbar-left">
            <DatePicker
              className="daily-report-rangepicker"
              size="large"
              format={DATE_PICKER_FORMAT}
              value={date}
              onChange={handleDateChange}
              allowClear={false}
            />
            <Button
              size="large"
              icon={<DownloadOutlined />}
              onClick={handleGeneratePdf}
              loading={isGeneratingPdf}
              disabled={!localInspection?._id || isLoading || isGeneratingPdf}
            >
              Export PDF
            </Button>
          </div>

          <div className="daily-report-toolbar-right">
            <Button
              size="large"
              icon={<SettingOutlined style={{ fontSize: 18 }} />}
              onClick={() => setIsQuestionsModalVisible(true)}
            >
              {questionsButtonLabel}
            </Button>
          </div>
        </div>

        <Text type="secondary" className="daily-report-range-hint">
          One safety report for {date.format(DATE_PICKER_FORMAT)}. Question-list
          edits apply only to today's report.
        </Text>
      </div>

      {isLoading && !localInspection?._id ? (
        <Text type="secondary">Loading safety report…</Text>
      ) : (
        <div className="mt-4">
          <SafetyQuestionsTable
            inspection={localInspection}
            saveAnswerAsync={updateInspectionAsync}
            onAnswerPersisted={handleAnswerPersisted}
          />

          <Title className="mt-6" level={3}>
            Attachments
          </Title>
          <AttachmentsSection
            projectId={projectId}
            value={localInspection?.attachments || []}
            onChange={handleAttachmentsChange}
            label="Add Attachments"
            title="Upload Attachments"
            description="Upload your attachments here."
            accept=".jpg,.jpeg,.png,.heic,.pdf"
          />

          <NotesTable
            dailyReport={localInspection}
            updateDailyReportMutation={({ dailyReport, dailyReportId }) =>
              updateInspectionMutation({
                inspectionId: dailyReportId,
                inspection: dailyReport,
              })
            }
          />
        </div>
      )}

      <SafetyQuestionsModal
        visible={isQuestionsModalVisible}
        onClose={handleQuestionsModalClose}
      />
    </div>
  );
};

export default Safety;
